import type { JSX } from 'react';
import { styled } from 'styled-components';
import { Box, Image } from '@wistia/vhs';
import { Text, Button, Icon } from '@wistia/ui';
import { trackStructEvent } from '~/utilities/snowplow';
import { downloadFile } from '~/utilities/downloadFile';
import type { DownloadableMediaFile } from '~/singlePageApp/components/DownloadModal';

export const Wrapper = styled.div`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.color.grey300};
  border-radius: 16px;
  display: flex;
  gap: 1rem;
  padding: 0.5rem;
  width: 100%;
`;

const ImageWrapper = styled.div`
  background-color: black;
  border-radius: 16px;
  height: 120px;
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const StyledImage = styled(Image)`
  height: auto;
  max-height: 120px;
  max-width: 200px;
`;

type Props = {
  downloadableMediaFile: DownloadableMediaFile;
};

export const StoryboardDownloadRow = ({ downloadableMediaFile }: Props): JSX.Element => {
  const { width, height, downloadUrl, displayName, extension } = downloadableMediaFile;

  const downloadImage = async () => {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    downloadFile(blob, `${displayName}.${extension}`, `image/${extension}`);
  };

  const handleDownload = () => {
    void downloadImage();

    trackStructEvent({
      category: 'button',
      action: 'click',
      label: 'publish-download-storyboard',
    });
  };

  return (
    <Wrapper>
      <ImageWrapper>
        <StyledImage
          src={downloadUrl}
          alt={displayName}
        />
      </ImageWrapper>
      <Box
        direction="column"
        gap="0.5rem"
      >
        <Text variant="body2">
          <strong>{displayName}</strong>
        </Text>

        <Text
          variant="body3"
          prominence="secondary"
        >
          {(extension ?? 'JPG').toUpperCase()} · {width} x {height}
        </Text>

        <Button
          onClick={handleDownload}
          leftIcon={<Icon type="download" />}
          data-testid="download-storyboard"
        >
          Download
        </Button>
      </Box>
    </Wrapper>
  );
};
